import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Youtube, Phone, MessageCircle, MapPin, Mail } from "lucide-react";
import { BRAND, PHONES, WHATSAPP_URL, SOCIALS, SERVICES, LOCATIONS } from "@/constants/content";

export default function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-300 pt-20 pb-8" data-testid="footer">
      <div className="container-page">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-10">
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={BRAND.logo} alt="KinFlex" className="h-11 w-11 rounded-xl object-cover ring-1 ring-white/10" />
              <div className="leading-tight">
                <div className="font-display font-bold text-white text-lg">KinFlex</div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: '#4dabff' }}>Home Healthcare</div>
              </div>
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
              India's premium home healthcare — 24×7 verified nurses, ICU-grade critical care, physiotherapy, elder care and medical equipment across Delhi NCR.
            </p>
            <div className="flex gap-2 mt-6">
              <a href={SOCIALS.instagram} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-lg bg-white/5 hover:bg-[#0A84FF] flex items-center justify-center transition-colors" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
              <a href={SOCIALS.linkedin} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-lg bg-white/5 hover:bg-[#0A84FF] flex items-center justify-center transition-colors" aria-label="LinkedIn">
                <Linkedin className="h-4 w-4" />
              </a>
              <a href={SOCIALS.youtube} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-lg bg-white/5 hover:bg-[#0A84FF] flex items-center justify-center transition-colors" aria-label="YouTube">
                <Youtube className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white text-xs uppercase tracking-[0.16em] mb-4">Quick Links</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" className="text-slate-400 hover:text-[#0A84FF] transition-colors">Home</Link></li>
              <li><Link to="/about" className="text-slate-400 hover:text-[#0A84FF] transition-colors">About</Link></li>
              <li><Link to="/services" className="text-slate-400 hover:text-[#0A84FF] transition-colors">Services</Link></li>
              <li><Link to="/medical-equipment" className="text-slate-400 hover:text-[#0A84FF] transition-colors">Medical Equipment</Link></li>
              <li><Link to="/blogs" className="text-slate-400 hover:text-[#0A84FF] transition-colors">Blogs</Link></li>
              <li><Link to="/careers" className="text-slate-400 hover:text-[#0A84FF] transition-colors">Careers</Link></li>
              <li><Link to="/contact" className="text-slate-400 hover:text-[#0A84FF] transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white text-xs uppercase tracking-[0.16em] mb-4">Services</h4>
            <ul className="space-y-2.5 text-sm">
              {SERVICES.slice(0, 8).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-slate-400 hover:text-[#0A84FF] transition-colors">{s.title}</Link>
                </li>
              ))}
              <li><Link to="/services" className="text-[#0A84FF] font-medium">View all →</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white text-xs uppercase tracking-[0.16em] mb-4">Locations</h4>
            <ul className="space-y-2.5 text-sm">
              {LOCATIONS.map((l) => (
                <li key={l.slug}>
                  <Link to={`/locations/${l.slug}`} className="text-slate-400 hover:text-[#0A84FF] transition-colors">
                    Home Healthcare in {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white text-xs uppercase tracking-[0.16em] mb-4">Contact</h4>
            <div className="space-y-2.5 text-sm">
              {PHONES.map((p) => (
                <a key={p} href={`tel:${p}`} className="flex items-center gap-2 text-slate-300 hover:text-[#0A84FF] transition-colors">
                  <Phone className="h-3.5 w-3.5" style={{ color: '#10B981' }} /> {p}
                </a>
              ))}
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-300 hover:text-[#25D366] transition-colors">
                <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" /> WhatsApp
              </a>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 text-slate-400 hover:text-[#0A84FF] transition-colors">
                <Mail className="h-3.5 w-3.5" /> {BRAND.email}
              </a>
              <div className="flex items-start gap-2 text-slate-500 pt-2 text-xs">
                <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Serving Delhi NCR — Delhi, Noida, Gurugram, Ghaziabad, Faridabad
              </div>
            </div>
          </div>
        </div>

          <div className="mt-14 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} KinFlex Home Healthcare. All rights reserved.</div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 justify-center">
            <Link to="/privacy-policy" className="hover:text-[#0A84FF]">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-[#0A84FF]">Terms</Link>
            <Link to="/refund-policy" className="hover:text-[#0A84FF]">Refund</Link>
            <Link to="/cancellation-policy" className="hover:text-[#0A84FF]">Cancellation</Link>
            <Link to="/medical-disclaimer" className="hover:text-[#0A84FF]">Disclaimer</Link>
            <Link to="/testimonials" className="hover:text-[#0A84FF]">Testimonials</Link>
            <Link to="/gallery" className="hover:text-[#0A84FF]">Gallery</Link>
            <Link to="/faqs" className="hover:text-[#0A84FF]">FAQs</Link>
            <Link to="/admin/login" className="hover:text-[#0A84FF]">Admin</Link>
          </div>
        </div>

        <div className="mt-4 text-[11px] text-slate-600 text-center max-w-3xl mx-auto leading-relaxed">
          Medical Disclaimer: KinFlex Home Healthcare provides supportive nursing and caregiving services. All medical decisions must be made by qualified physicians. Our staff operates under the guidance of the treating doctor.
        </div>
      </div>
    </footer>
  );
}
